import React from 'react';
import UnitPageShell from '../../components/shared/UnitPageShell';
import EditableTextBlock from '../../components/shared/EditableTextBlock';
import ZoomableImage from '../../components/shared/ZoomableImage';
import { Box, Button, IconButton } from '@mui/material';
import { VolumeUp as SpeakerIcon } from '@mui/icons-material';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../../context/AuthContext';
import { useTTS } from '../../hooks/useTTS';
import { uploadUnitAsset, updateUnitContent, useUnitOverride } from '../../services/unitOverridesService';

const PAGE_ID = 'm1_u1_x6_hxdiagram';

const M1_U1_X6_HxDiagram: React.FC = () => {
  const navigate = useNavigate();
  const { token, canEditTextsM1, canUploadImagesM1 } = useAuth();
  const { override, refresh } = useUnitOverride(PAGE_ID);

  const textBlocks = React.useMemo(() => ([
    { id: 'block_was_ist_hx', heading: '1. Was ist das h-x-Diagramm?', text: override?.texts?.['block_was_ist_hx'] ?? 'Grafik zur Darstellung Energiegehalt (h) und Wassergehalt (x) feuchter Luft für Temperatur-Zustände.' },
    { id: 'block_achsen', heading: '2. Die Achsen des Diagramms', text: override?.texts?.['block_achsen'] ?? 'Untere Achse: Wassergehalt (x) g/kg trockene Luft. Linke Achse: Temperatur (t) °C.' },
    { id: 'block_linien', heading: '3. Die Linien im Diagramm', text: override?.texts?.['block_linien'] ?? 'Relative Luftfeuchte-Kurven (0–100%), Isothermen (gleich t), Isenthalpen (gleich h).' },
    { id: 'block_verwendung', heading: '4. Verwendung', text: override?.texts?.['block_verwendung'] ?? 'Zustandspunkte bestimmen Prozesse: Heizen/Kühlen (horizontal), Befeuchten/Entfeuchten (vertikal), Mischen (Verbindungslinie).' },
    { id: 'block_wozu', heading: '5. Nutzen', text: override?.texts?.['block_wozu'] ?? 'Auslegung Lüftungs-/Klimaprozesse: Leistungsbedarf, Taupunkt, Befeuchtung – Kernwerkzeug der Lüftungstechnik.' }
  ]), [override]);

  const fullPlainText = React.useMemo(() => {
    return textBlocks.map(b => `${b.heading}. ${b.text}`).join(' ');
  }, [textBlocks]);

  const { speak, stop, speaking } = useTTS({ text: fullPlainText });

  const handleImageReplace = React.useCallback(async (imageKey: string, file: File) => {
    if (!token) return alert('Kein Token – bitte einloggen.');
    if (!canUploadImagesM1) return alert('Keine Berechtigung fuer Bilder-Uploads.');
    try {
      await uploadUnitAsset(PAGE_ID, imageKey, file, token);
      alert('Bild erfolgreich aktualisiert.');
      refresh();
    } catch (e: any) {
      alert('Upload fehlgeschlagen: ' + (e?.message || e));
    }
  }, [token, refresh, canUploadImagesM1]);

  const handleTextSave = React.useCallback(async (blockId: string, newBody: string) => {
    if (!token) return alert('Kein Token – bitte einloggen.');
    if (!canEditTextsM1) return alert('Keine Berechtigung zum Texte bearbeiten.');
    try {
      await updateUnitContent(PAGE_ID, { texts: { [blockId]: newBody } }, token);
      alert('Text gespeichert.');
      refresh();
    } catch (e: any) {
      alert('Speichern fehlgeschlagen: ' + (e?.message || e));
    }
  }, [token, refresh, canEditTextsM1]);

  const ttsButton = React.createElement(IconButton, {
    onClick: speaking ? stop : speak,
    'aria-label': 'Text vorlesen',
    sx: { ml: 1 }
  }, React.createElement(SpeakerIcon, { sx: { fontSize: 28 } }));

  return React.createElement(
    UnitPageShell,
    { title: '1.1.6 h-x-Diagramm', actions: ttsButton },
    React.createElement(Box, { sx: { display: 'grid', gridTemplateColumns: { xs: '1fr', md: '1fr 1fr' }, gap: 3 } },
      React.createElement(Box, { sx: { display: 'flex', flexDirection: 'column', gap: 2 } },
        React.createElement(ZoomableImage, {
          src: (() => { const v = override?.images?.['img_0']; if (!v) return '/assets/drawable/mollier_diagramm_dollier_diagramm.jpg'; const s = String(v); return s.startsWith('/assets/') || s.startsWith('assets/') ? s : `/assets/unit_custom/${s}`; })(),
          alt: 'h-x-Diagramm (Mollier-Diagramm)',
          label: 'Bild einfügen',
          onReplace: canUploadImagesM1 ? (file: File) => handleImageReplace('img_0', file) : undefined
        })
      ),
      React.createElement(Box, { sx: { display: 'flex', flexDirection: 'column', gap: 2 } },
        textBlocks.map((block) => React.createElement(EditableTextBlock, {
          key: block.id,
          pageId: PAGE_ID,
          blockId: block.id,
          heading: block.heading,
          body: block.text,
          onSave: (newBody: string) => handleTextSave(block.id, newBody)
        })),
        React.createElement(Box, { sx: { mt: 3 } },
          React.createElement(Button, { variant: 'outlined', onClick: () => navigate('/units/m1_u1_x99_quiz') }, 'Zum Quiz')
        )
      )
    )
  );
};

export default M1_U1_X6_HxDiagram;

